import {AuthUser, LoginRequest, LoginResponse} from '../types/Auth';
import httpClient from './httpClient';
import {handleError} from '../utils/errorHandling';
import {UserRole} from '../../admin/users/types/User';

class AuthService {
  private readonly TOKEN_KEY = 'authToken';

  public async login(email: string, password: string): Promise<string> {
    const loginRequest: LoginRequest = { email, password };
    
    return handleError(
      async () => {
        const response: LoginResponse = await httpClient.loginRequest('/auth/login', loginRequest);
        this.setToken(response.token);
        return response.token;
      },
      'Error during login:'
    );
  }

  public async register(request: LoginRequest): Promise<void> {
    return handleError(
      async () => await httpClient.post<void>('/auth/register', request),
      'Error during registration:'
    );
  }

  public async confirmEmail(id: string, token: string): Promise<void> {
    return handleError(
      async () => {
        await httpClient.post<void>(`/auth/confirm-email?id=${id}&token=${token}`);
      },
      'Error during email confirmation:'
    );
  }

  public async requestPasswordReset(email: string): Promise<void> {
    return handleError(
      async () => {
        await httpClient.post<void>('/auth/reset', { email });
      },
      'Error during password reset request:'
    );
  }

  public async resetPasswordWithToken(id: string, token: string, password: string): Promise<void> {
    return handleError(
      async () => {
        await httpClient.post<void>(`/auth/reset-password?id=${id}&token=${token}`, { password });
      },
      'Error during password reset:'
    );
  }

  public logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
  }

  public isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    try {
      const payload = this.parseTokenPayload(token);
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch {
      return false;
    }
  }

  public getCurrentUser(): AuthUser | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    try {
      const payload = this.parseTokenPayload(token);
      return {
        email: payload.sub,
        role: payload.role as UserRole
      };
    } catch {
      return null;
    }
  }

  private setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  private getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  private parseTokenPayload(token: string): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  }
}

const authService = new AuthService();
export default authService;
